import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, ChevronRight, Flame, Wind, Anchor, Layers, Wrench,
  CheckCircle2, Phone, Mail, Download, AlertTriangle
} from 'lucide-react';
import { ROUTE_PATHS } from '@/lib/index';
import { IMAGES } from '@/assets/images';

/* ── 浅色工业风色值 ── */
const ORANGE = 'oklch(0.66 0.19 42)';
const CARD_BG = '#FFFFFF';
const BORDER = '#D0D5E2';
const MUTED_TEXT = '#5A6780';
const DEEP_BG = '#F0F2F5';

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } },
};
const stagger = { show: { transition: { staggerChildren: 0.08 } } };

/* ── 技术参数 ── */
const techParams = [
  { test: '拉伸强度', standard: 'ISO 37-2011 / ASTM D412', value: '≥10 MPa', note: '标准配方实测值' },
  { test: '断裂伸长率', standard: 'ISO 37-2011 / ASTM D412', value: '≥300%', note: '—' },
  { test: '硬度', standard: 'ISO 48-2010 / ASTM D2240', value: '65±5 Shore A', note: '可定制 55A～75A' },
  { test: '比重', standard: 'ISO 2781-2008', value: '1.23～1.30 g/cm³', note: '依配方略有差异' },
  { test: '工作温度范围', standard: '—', value: '-35°C 至 +100°C', note: '间歇接触可至 +120°C' },
  { test: '阻燃等级', standard: 'UL 94 / ISO 4589', value: 'V-1 ～ V-0（依配方）', note: '固有阻燃，无需添加剂' },
  { test: '耐臭氧老化', standard: 'ISO 1431 / ASTM D1149', value: '优良（50 pphm×168h 无裂纹）', note: '户外长期使用' },
  { test: '耐海水浸泡体积变化', standard: 'ISO 1817 / ASTM D471', value: '<8%（参考值）', note: '25°C×168h 海水浸泡' },
];

/* ── 核心特性 ── */
const coreFeatures = [
  {
    icon: Flame,
    title: '固有阻燃性',
    titleEn: 'INHERENT FLAME RESISTANCE',
    desc: 'CR 分子链含氯原子，无需添加阻燃剂即具备固有阻燃性（UL 94 V-1 起），离火后自熄，是船舶、建筑及矿井阻燃要求场合中应用最广的通用橡胶材料之一。',
  },
  {
    icon: Wind,
    title: '耐候抗臭氧老化',
    titleEn: 'WEATHER & OZONE RESISTANCE',
    desc: '对臭氧、紫外线和大气老化的耐受性明显优于 NR 和 NBR，在户外暴露环境中长期使用不易开裂粉化，适合建筑密封、桥梁支座等需要长寿命户外性能的场合。',
  },
  {
    icon: Anchor,
    title: '耐海水与盐雾腐蚀',
    titleEn: 'SEAWATER RESISTANCE',
    desc: '对海水、盐雾及稀酸碱具有良好耐受性，在海洋工程、港口码头及船舶密封等涉海场合中性能稳定，是替代 NR 用于涉海密封的优选材料。',
  },
  {
    icon: Layers,
    title: '优异的粘接性',
    titleEn: 'EXCELLENT ADHESION',
    desc: 'CR 与金属、织物及其他橡胶的粘接性能出色，可直接与钢板硫化粘合制造复合构件，也适合用作粘合层基材，是橡胶-金属复合密封件的常用材料。',
  },
  {
    icon: Wrench,
    title: '中等耐油性',
    titleEn: 'MODERATE OIL RESISTANCE',
    desc: '对矿物油有一定耐受能力（优于 NR 和 EPDM），可用于偶尔接触润滑油的场合，但不适合长期浸泡于石油基液压油或燃油中，后者应优先选用 NBR 或 FKM。',
  },
];

/* ── 应用场景 ── */
interface AppItem {
  num: string;
  title: string;
  titleEn: string;
  desc: string;
  details: string[];
  img: string | null;
  imgAlt: string;
}

const applications: AppItem[] = [
  {
    num: '①',
    title: '船舶舱室与甲板密封',
    titleEn: 'MARINE HULL & DECK SEALING',
    desc: '船舶机舱、货舱舱口盖、水密舱壁门及甲板开孔等部位，密封材料需同时满足阻燃（SOLAS 法规）、耐海水浸泡和耐疲劳振动要求。CR 的固有阻燃性和耐海水性使其成为符合 IMO/SOLAS 认证要求的优选船用密封橡胶。',
    details: ['舱口盖密封条', '水密舱壁门垫', '甲板贯通密封', '机舱管路防振垫'],
    img: IMAGES.CR_MARINE_SEALING,
    imgAlt: '船舶舱室CR密封应用',
  },
  {
    num: '②',
    title: '建筑防火隔断与幕墙密封',
    titleEn: 'BUILDING FIRE BARRIERS',
    desc: '建筑物防火分区隔断、防火门密封条、防火卷帘侧导轨密封及幕墙系统的防火节点填充，要求材料在火焰接触时能有效阻止烟气和火焰蔓延。CR 的固有阻燃性（无卤、低烟）使其成为建筑防火密封的主流材料。',
    details: ['防火门密封条', '防火分区隔断垫', '幕墙防火节点填充', '防火卷帘侧导轨垫'],
    img: IMAGES.CR_BUILDING_FIREPROOF,
    imgAlt: '建筑防火隔断CR密封',
  },
  {
    num: '③',
    title: '电缆护套与线槽密封',
    titleEn: 'CABLE PROTECTION',
    desc: '电力电缆、控制电缆的护套、绝缘层及贯穿墙体的电缆密封件，在隧道、船舶、矿井和建筑竖井中使用时需满足阻燃要求。CR 的阻燃性和良好的绝缘性能（在不含导电填料时）使其适合用于阻燃电缆护套和密封。',
    details: ['阻燃电缆护套', '电缆贯穿密封件', '控制电缆绝缘垫', '线槽防火密封垫'],
    img: IMAGES.CR_OFFSHORE_MARINE,
    imgAlt: '电缆保护CR护套应用',
  },
  {
    num: '④',
    title: '桥梁板式支座与减震垫',
    titleEn: 'BRIDGE BEARINGS',
    desc: '公路、铁路桥梁的板式橡胶支座需要在数十年内承受静载与动载，同时适应温度变化引起的热胀冷缩位移。CR 良好的耐候性、抗臭氧性和适中的弹性模量，使其成为 NR 支座之外的替代选择，尤其适用于沿海高湿盐雾环境的桥梁工程。',
    details: ['公路桥梁板式支座', '沿海桥梁减震垫', '铁路桥梁防振垫', '城市高架桥支座'],
    img: IMAGES.P15_NR_BRIDGE_1_198,
    imgAlt: '桥梁支座CR减震应用',
  },
  {
    num: '⑤',
    title: '海洋工程与港口防护',
    titleEn: 'MARINE & PORT PROTECTION',
    desc: '海上钻井平台、港口岸线设施及近海管道的密封和防护涂层，长期暴露于高盐雾、海水飞溅和强紫外线环境中。CR 在这种复合腐蚀环境下优于 NR，其耐候性和耐海水性确保密封件和防护衬层长期稳定工作。',
    details: ['海上平台管道密封', '港口护岸防护垫', '近海管道防腐包覆', '海洋仪器防水密封'],
    img: IMAGES.P17_NR_PORT_1_1_212,
    imgAlt: '港口海洋工程CR防护应用',
  },
];

/* ── CR vs 同类橡胶对比 ── */
const comparison = [
  { prop: '固有阻燃性', cr: '★★★★★', nbr: '★☆☆☆☆', epdm: '★★☆☆☆', nr: '★☆☆☆☆' },
  { prop: '耐候性', cr: '★★★★☆', nbr: '★★☆☆☆', epdm: '★★★★★', nr: '★★☆☆☆' },
  { prop: '耐海水性', cr: '★★★★★', nbr: '★★★☆☆', epdm: '★★★☆☆', nr: '★★☆☆☆' },
  { prop: '粘接性', cr: '★★★★★', nbr: '★★★☆☆', epdm: '★★☆☆☆', nr: '★★★★☆' },
  { prop: '耐油性', cr: '★★★☆☆', nbr: '★★★★★', epdm: '★☆☆☆☆', nr: '★☆☆☆☆' },
  { prop: '低温柔性', cr: '★★★★☆', nbr: '★★★★☆', epdm: '★★★★☆', nr: '★★★★★' },
];

/* ── 定制能力 ── */
const customFormulas = [
  { dim: '阻燃等级', standard: 'UL 94 V-1', custom: '可升级至 V-0 配方', use: '高阻燃要求场合' },
  { dim: '硬度', standard: '65±5 Shore A', custom: '可向 55A 或 75A 调整', use: '软密封条或高承载垫' },
  { dim: '低温等级', standard: '-35°C 柔性保证', custom: '可评估至 -45°C（低温配方）', use: '寒区船舶或高寒桥梁' },
  { dim: '颜色', standard: '黑色', custom: '特定颜色（定制）', use: '颜色编码或外观要求' },
  { dim: '粘接面处理', standard: '纯板材', custom: '可提供金属硫化粘合复合件', use: '橡胶-金属复合密封件' },
  { dim: '厚度 / 幅宽', standard: '1.0～20mm / 标准幅宽', custom: '可定制裁切尺寸', use: '异形密封条或大幅板' },
];

/* ── 产品规格 ── */
const specList = [
  { label: '颜色', value: '黑色（标准）' },
  { label: '厚度范围', value: '1.0mm 至 20.0mm' },
  { label: '幅宽', value: '0.91m 至 2m（标准），可定制' },
  { label: '硬度区间', value: '55A ～ 75A Shore A（可定制）' },
  { label: '阻燃等级', value: 'UL 94 V-1（标准）/ V-0（选配）' },
  { label: '认证参考', value: 'IMO/SOLAS 船用认证（可协助申请）' },
  { label: '定制服务', value: '裁切、定制尺寸、橡胶-金属复合件' },
];

export default function ProductCR() {
  return (
    <div className="bg-background text-foreground min-h-screen">

      {/* ── 面包屑 ── */}
      <div className="border-b border-border" style={{ background: DEEP_BG }}>
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center gap-2 text-xs" style={{ color: MUTED_TEXT }}>
            <Link to="/" className="hover:text-primary transition-colors">首页</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to={ROUTE_PATHS.PRODUCTS} className="hover:text-primary transition-colors">产品系列</Link>
            <ChevronRight className="w-3 h-3" />
            <span style={{ color: ORANGE }}>CR 氯丁橡胶板</span>
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <section className="relative overflow-hidden" style={{ background: DEEP_BG, minHeight: 420 }}>
        <img
          src={IMAGES.CR_RUBBER_SHEET_CLOSEUP}
          alt="CR氯丁橡胶板"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: 0.12 }}
        />
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(135deg, rgba(15,30,53,0.90) 0%, rgba(15,30,53,0.65) 100%)' }}
        />
        <div className="relative container mx-auto px-4 py-16 lg:py-24">
          <motion.div initial="hidden" animate="show" variants={stagger}>
            <motion.div variants={fadeUp} className="mb-8">
              <Link
                to={ROUTE_PATHS.PRODUCTS}
                className="inline-flex items-center gap-2 text-sm transition-colors"
                style={{ color: MUTED_TEXT }}
              >
                <ArrowLeft className="w-4 h-4" />
                返回产品列表
              </Link>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* 左：文字 */}
              <div>
                <motion.div variants={fadeUp} className="flex items-center gap-3 mb-4">
                  <span
                    className="px-3 py-1 text-xs font-black tracking-[0.2em] uppercase rounded-sm"
                    style={{ background: 'oklch(0.66 0.19 42 / 0.15)', color: ORANGE, border: `1px solid ${ORANGE}` }}
                  >
                    CR
                  </span>
                  <span className="text-xs tracking-widest uppercase" style={{ color: MUTED_TEXT }}>
                    CHLOROPRENE RUBBER · NEOPRENE
                  </span>
                </motion.div>

                <motion.h1 variants={fadeUp} className="text-4xl lg:text-5xl font-black mb-4 leading-tight">
                  <span style={{ color: ORANGE }}>CR</span> 氯丁橡胶板
                </motion.h1>
                <motion.p variants={fadeUp} className="text-lg font-medium mb-2" style={{ color: 'oklch(0.75 0.010 225)' }}>
                  船舶、建筑与海洋工程的固有阻燃首选密封材料
                </motion.p>
                <motion.p variants={fadeUp} className="text-sm leading-relaxed mb-8" style={{ color: MUTED_TEXT }}>
                  CR 氯丁橡胶（氯丁二烯橡胶，又称 Neoprene）以其分子链中的氯原子赋予的
                  <strong className="text-foreground">固有阻燃性</strong>为核心优势，
                  同时具备优良的耐候性、耐海水腐蚀性和优异的粘接性能，
                  是船舶密封、建筑防火和海洋工程领域不可替代的工业橡胶材料。
                  工作温度范围 -35°C 至 +100°C，阻燃等级可达 UL 94 V-0。
                </motion.p>

                <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
                  <Link
                    to={ROUTE_PATHS.CONTACT}
                    className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold rounded-sm"
                    style={{ background: ORANGE, color: '#FFFFFF' }}
                  >
                    <Phone className="w-4 h-4" />
                    立即询价
                  </Link>
                  <Link
                    to={`${ROUTE_PATHS.TECH_SPECS}#cr`}
                    className="inline-flex items-center gap-2 px-6 py-3 text-sm font-bold rounded-sm"
                    style={{ border: `1px solid ${BORDER}`, color: 'oklch(0.88 0.008 220)' }}
                  >
                    查看技术参数
                  </Link>
                </motion.div>
              </div>

              {/* 右：图 + 规格卡 */}
              <motion.div variants={fadeUp} className="flex flex-col gap-4">
                <div className="rounded overflow-hidden" style={{ border: `1px solid ${BORDER}` }}>
                  <img
                    src={IMAGES.CR_RUBBER_SHEET_CLOSEUP}
                    alt="CR氯丁橡胶板材实物"
                    className="w-full object-cover"
                    style={{ maxHeight: 260, objectPosition: 'center' }}
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: '工作温度', value: '-35°C ~ +100°C', sub: '间歇可至 +120°C' },
                    { label: '阻燃等级', value: 'UL 94 V-1/V-0', sub: '固有阻燃，无需添加剂' },
                    { label: '板材厚度', value: '1.0 ~ 20.0mm', sub: '幅宽 0.91m ～ 2m' },
                  ].map((s) => (
                    <div
                      key={s.label}
                      className="rounded p-3 text-center"
                      style={{ background: CARD_BG, border: `1px solid ${BORDER}` }}
                    >
                      <p className="text-[10px] tracking-widest uppercase mb-1" style={{ color: MUTED_TEXT }}>{s.label}</p>
                      <p className="text-sm font-bold leading-tight" style={{ color: ORANGE }}>{s.value}</p>
                      <p className="text-[10px] mt-0.5" style={{ color: MUTED_TEXT }}>{s.sub}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── 核心特性 ── */}
      <section className="py-16 lg:py-20" style={{ background: '#F0F2F5' }}>
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="mb-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1" style={{ background: BORDER }} />
                <span className="text-xs font-bold tracking-[0.25em] uppercase" style={{ color: ORANGE }}>CORE PROPERTIES</span>
                <div className="h-px flex-1" style={{ background: BORDER }} />
              </div>
              <h2 className="text-2xl font-black text-center">核心特性</h2>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {coreFeatures.map((f) => {
                const Icon = f.icon;
                return (
                  <motion.div
                    key={f.title}
                    variants={fadeUp}
                    className="rounded p-5 flex flex-col gap-3"
                    style={{ background: CARD_BG, border: `1px solid ${BORDER}` }}
                  >
                    <div
                      className="w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0"
                      style={{ background: 'oklch(0.66 0.19 42 / 0.12)', border: '1px solid oklch(0.66 0.19 42 / 0.3)' }}
                    >
                      <Icon className="w-5 h-5" style={{ color: ORANGE }} />
                    </div>
                    <div>
                      <p className="text-[9px] font-bold tracking-widest uppercase mb-0.5" style={{ color: MUTED_TEXT }}>{f.titleEn}</p>
                      <p className="text-sm font-bold mb-2">{f.title}</p>
                      <p className="text-xs leading-relaxed" style={{ color: MUTED_TEXT }}>{f.desc}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── 技术参数 ── */}
      <section id="tech-params" className="py-16 lg:py-20" style={{ background: DEEP_BG }}>
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="mb-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1" style={{ background: BORDER }} />
                <span className="text-xs font-bold tracking-[0.25em] uppercase" style={{ color: ORANGE }}>TECHNICAL DATA</span>
                <div className="h-px flex-1" style={{ background: BORDER }} />
              </div>
              <h2 className="text-2xl font-black text-center">CR 氯丁橡胶板物理性能数据</h2>
              <p className="text-sm text-center mt-2" style={{ color: MUTED_TEXT }}>
                所有数值均为标准配方典型实测值，依据 ISO / ASTM / UL 标准体系测试
              </p>
            </motion.div>

            {/* 核心指标高亮 */}
            <motion.div variants={fadeUp} className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {[
                { label: '拉伸强度', value: '≥10 MPa', sub: 'ISO 37 / ASTM D412' },
                { label: '断裂伸长率', value: '≥300%', sub: '标准配方' },
                { label: '阻燃等级', value: 'UL 94 V-1/V-0', sub: '固有阻燃无需添加剂' },
                { label: '工作温度', value: '-35 ~ +100°C', sub: '间歇可至 +120°C' },
              ].map((m) => (
                <div
                  key={m.label}
                  className="rounded p-5 text-center"
                  style={{ background: CARD_BG, border: '1px solid oklch(0.66 0.19 42 / 0.25)' }}
                >
                  <p className="text-2xl font-black mb-1" style={{ color: ORANGE }}>{m.value}</p>
                  <p className="text-sm font-semibold mb-1">{m.label}</p>
                  <p className="text-[10px] font-mono" style={{ color: MUTED_TEXT }}>{m.sub}</p>
                </div>
              ))}
            </motion.div>

            {/* 完整参数表 */}
            <motion.div variants={fadeUp} className="overflow-x-auto rounded" style={{ border: `1px solid ${BORDER}` }}>
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: '#1A2337', borderBottom: `2px solid ${BORDER}` }}>
                    {['测试项目', '测试标准', '典型数值', '参考说明'].map((h) => (
                      <th key={h} className="px-5 py-3.5 text-left text-xs font-black tracking-widest uppercase" style={{ color: ORANGE }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {techParams.map((row, i) => (
                    <tr
                      key={row.test}
                      style={{ background: i % 2 === 0 ? CARD_BG : '#F0F2F5', borderBottom: `1px solid ${BORDER}` }}
                    >
                      <td className="px-5 py-3.5 font-semibold">{row.test}</td>
                      <td className="px-5 py-3.5 font-mono text-xs" style={{ color: MUTED_TEXT }}>{row.standard}</td>
                      <td className="px-5 py-3.5 font-bold" style={{ color: ORANGE }}>{row.value}</td>
                      <td className="px-5 py-3.5 text-xs" style={{ color: MUTED_TEXT }}>{row.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </motion.div>
            <motion.div variants={fadeUp} className="mt-5 flex items-start gap-2 text-xs" style={{ color: MUTED_TEXT }}>
              <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: ORANGE }} />
              <span>以上数据为标准配方典型实测值。低温配方可将脆化温度降至 -45°C，高阻燃配方可达 UL 94 V-0，请联系技术团队根据具体工况确认配方选型。</span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── 应用场景 ── */}
      <section className="py-16 lg:py-20" style={{ background: '#F0F2F5' }}>
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="mb-12">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1" style={{ background: BORDER }} />
                <span className="text-xs font-bold tracking-[0.25em] uppercase" style={{ color: ORANGE }}>CR APPLICATIONS</span>
                <div className="h-px flex-1" style={{ background: BORDER }} />
              </div>
              <h2 className="text-2xl font-black text-center">经过验证的主要应用领域</h2>
            </motion.div>

            <div className="space-y-8">
              {applications.map((app, idx) => (
                <motion.div
                  key={app.num}
                  variants={fadeUp}
                  className="rounded overflow-hidden"
                  style={{ background: CARD_BG, border: `1px solid ${BORDER}` }}
                >
                  <div className={`grid grid-cols-1 lg:grid-cols-2 ${idx % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                    <div
                      className={`relative overflow-hidden ${idx % 2 === 1 ? 'lg:order-2' : ''}`}
                      style={{ minHeight: 260 }}
                    >
                      {app.img ? (
                        <img
                          src={app.img}
                          alt={app.imgAlt}
                          className="absolute inset-0 w-full h-full object-cover"
                          style={{ opacity: 0.88 }}
                        />
                      ) : (
                        <div
                          className="absolute inset-0 flex flex-col items-center justify-center gap-2"
                          style={{ background: '#ECEEF2', border: `2px dashed ${BORDER}` }}
                        >
                          <AlertTriangle className="w-8 h-8" style={{ color: MUTED_TEXT }} />
                          <p className="text-xs font-mono" style={{ color: MUTED_TEXT }}>图片位置保留</p>
                        </div>
                      )}
                      <div
                        className="absolute top-4 left-4 w-10 h-10 rounded-sm flex items-center justify-center font-black text-lg z-10"
                        style={{ background: ORANGE, color: '#FFFFFF' }}
                      >
                        {app.num}
                      </div>
                    </div>
                    <div className={`p-8 flex flex-col justify-center ${idx % 2 === 1 ? 'lg:order-1' : ''}`}>
                      <p className="text-[10px] font-bold tracking-widest uppercase mb-2" style={{ color: ORANGE }}>{app.titleEn}</p>
                      <h3 className="text-xl font-black mb-4">{app.title}</h3>
                      <p className="text-sm leading-relaxed mb-6" style={{ color: MUTED_TEXT }}>{app.desc}</p>
                      <div>
                        <p className="text-xs font-bold tracking-wider uppercase mb-3" style={{ color: MUTED_TEXT }}>典型用途</p>
                        <div className="grid grid-cols-2 gap-2">
                          {app.details.map((d) => (
                            <div key={d} className="flex items-center gap-2 text-xs">
                              <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: ORANGE }} />
                              {d}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── CR vs 主流橡胶对比 ── */}
      <section className="py-16" style={{ background: DEEP_BG }}>
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="mb-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1" style={{ background: BORDER }} />
                <span className="text-xs font-bold tracking-[0.25em] uppercase" style={{ color: ORANGE }}>MATERIAL COMPARISON</span>
                <div className="h-px flex-1" style={{ background: BORDER }} />
              </div>
              <h2 className="text-2xl font-black text-center">CR 与主流工业橡胶性能对比</h2>
              <p className="text-sm text-center mt-2 max-w-2xl mx-auto" style={{ color: MUTED_TEXT }}>
                CR 在阻燃性和耐海水性方面具有天然优势，是船舶与海洋工程的平衡型首选，兼顾多种性能需求。
              </p>
            </motion.div>

            <motion.div variants={fadeUp} className="overflow-x-auto rounded" style={{ border: `1px solid ${BORDER}` }}>
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: '#1A2337', borderBottom: `2px solid ${BORDER}` }}>
                    {['性能维度', 'CR 氯丁橡胶', 'NBR 丁腈橡胶', 'EPDM 三元乙丙', 'NR 天然橡胶'].map((h, i) => (
                      <th key={h} className="px-5 py-3.5 text-left text-xs font-black tracking-widest uppercase"
                        style={{ color: i === 1 ? ORANGE : 'oklch(0.70 0.015 225)' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {comparison.map((row, i) => (
                    <tr key={row.prop} style={{ background: i % 2 === 0 ? CARD_BG : '#F0F2F5', borderBottom: `1px solid ${BORDER}` }}>
                      <td className="px-5 py-3.5 font-semibold">{row.prop}</td>
                      <td className="px-5 py-3.5 font-bold" style={{ color: ORANGE }}>{row.cr}</td>
                      <td className="px-5 py-3.5" style={{ color: MUTED_TEXT }}>{row.nbr}</td>
                      <td className="px-5 py-3.5" style={{ color: MUTED_TEXT }}>{row.epdm}</td>
                      <td className="px-5 py-3.5" style={{ color: MUTED_TEXT }}>{row.nr}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── 定制配方 + 规格 ── */}
      <section className="py-16 lg:py-20" style={{ background: '#F0F2F5' }}>
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="mb-10">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px flex-1" style={{ background: BORDER }} />
                <span className="text-xs font-bold tracking-[0.25em] uppercase" style={{ color: ORANGE }}>CUSTOM FORMULATION & SPECS</span>
                <div className="h-px flex-1" style={{ background: BORDER }} />
              </div>
              <h2 className="text-2xl font-black text-center">定制配方能力与产品规格</h2>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              {/* 定制维度表 */}
              <motion.div variants={fadeUp}>
                <h3 className="text-sm font-bold uppercase tracking-widest mb-4" style={{ color: MUTED_TEXT }}>可调整配方维度</h3>
                <div className="overflow-x-auto rounded" style={{ border: `1px solid ${BORDER}` }}>
                  <table className="w-full text-xs">
                    <thead>
                      <tr style={{ background: '#1A2337', borderBottom: `2px solid ${BORDER}` }}>
                        {['调整维度', '标准值', '可调方向'].map((h) => (
                          <th key={h} className="px-4 py-3 text-left font-black tracking-widest uppercase" style={{ color: ORANGE }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {customFormulas.map((row, i) => (
                        <tr key={row.dim} style={{ background: i % 2 === 0 ? CARD_BG : '#F0F2F5', borderBottom: `1px solid ${BORDER}` }}>
                          <td className="px-4 py-3 font-semibold">{row.dim}</td>
                          <td className="px-4 py-3 font-mono" style={{ color: 'oklch(0.78 0.012 225)' }}>{row.standard}</td>
                          <td className="px-4 py-3 font-bold" style={{ color: ORANGE }}>{row.custom}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>

              {/* 规格 + 图 */}
              <motion.div variants={fadeUp} className="flex flex-col gap-6">
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded overflow-hidden" style={{ border: `1px solid ${BORDER}` }}>
                    <img src={IMAGES.CR_RUBBER_SHEET_PRODUCT} alt="CR橡胶板材展示" className="w-full h-40 object-cover" />
                  </div>
                  <div className="rounded overflow-hidden" style={{ border: `1px solid ${BORDER}` }}>
                    <img src={IMAGES.CR_RUBBER_SHEET_CLOSEUP} alt="CR橡胶板材规格" className="w-full h-40 object-cover" />
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-bold uppercase tracking-widest mb-4" style={{ color: MUTED_TEXT }}>标准产品规格</h3>
                  <div className="space-y-2">
                    {specList.map((spec) => (
                      <div
                        key={spec.label}
                        className="flex items-start gap-4 text-sm py-2.5"
                        style={{ borderBottom: `1px solid ${BORDER}` }}
                      >
                        <span className="w-28 flex-shrink-0 text-xs font-bold tracking-wider uppercase pt-0.5" style={{ color: MUTED_TEXT }}>
                          {spec.label}
                        </span>
                        <span className="font-semibold">{spec.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── 选型提示 ── */}
      <section className="py-10" style={{ background: DEEP_BG }}>
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
            <motion.div
              variants={fadeUp}
              className="rounded p-6 flex flex-col md:flex-row gap-5 items-start"
              style={{ background: CARD_BG, border: `1px solid oklch(0.66 0.19 42 / 0.3)` }}
            >
              <div
                className="w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0 mt-0.5"
                style={{ background: 'oklch(0.66 0.19 42 / 0.15)', border: `1px solid ${ORANGE}` }}
              >
                <CheckCircle2 className="w-5 h-5" style={{ color: ORANGE }} />
              </div>
              <div>
                <p className="text-sm font-bold mb-2" style={{ color: ORANGE }}>CR 选型重要提示</p>
                <p className="text-sm leading-relaxed" style={{ color: MUTED_TEXT }}>
                  CR 综合性能均衡，但<strong className="text-foreground">不耐强氧化性酸、酮类和酯类溶剂</strong>。
                  需要更强耐油性的场合应选 NBR；需要更高耐温（150°C+）的场合应选 FKM；
                  需要更强耐候性且不要求阻燃的户外密封场合可优先考虑 EPDM。
                  如有疑问，请联系我们的技术团队进行工况评估与材料推荐。
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-14" style={{ background: '#F0F2F5', borderTop: `1px solid ${BORDER}` }}>
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={stagger}
            className="max-w-3xl mx-auto text-center"
          >
            <motion.div variants={fadeUp} className="mb-4">
              <span className="text-xs font-bold tracking-widest uppercase" style={{ color: ORANGE }}>GET A QUOTE</span>
            </motion.div>
            <motion.h2 variants={fadeUp} className="text-2xl lg:text-3xl font-black mb-4">
              <>需要 CR 氯丁橡胶板？<br />我们提供技术选型与阻燃认证支持</>
            </motion.h2>
            <motion.p variants={fadeUp} className="text-sm mb-8" style={{ color: MUTED_TEXT }}>
              说明您的工况（阻燃等级要求、使用环境、规格尺寸），技术顾问将为您提供选型建议并安排样品。
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap items-center justify-center gap-4">
              <Link
                to={ROUTE_PATHS.CONTACT}
                className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-black rounded-sm"
                style={{ background: ORANGE, color: '#FFFFFF' }}
              >
                <Mail className="w-4 h-4" />
                提交询价
              </Link>
              <a
                href="tel:13426031477"
                className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-bold rounded-sm"
                style={{ border: `1px solid ${BORDER}`, color: 'oklch(0.88 0.008 220)' }}
              >
                <Phone className="w-4 h-4" />
                13426031477
              </a>
              <Link
                to={ROUTE_PATHS.SELECTION}
                className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-bold rounded-sm"
                style={{ border: `1px solid ${BORDER}`, color: 'oklch(0.88 0.008 220)' }}
              >
                <Download className="w-4 h-4" />
                查看选型矩阵
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

    </div>
  );
}
